import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Alert,
  MenuItem,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import api from '../services/api';

const AddContract = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    contract_number: '',
    contract_type: 'temporary',
    position_title: '',
    position_title_lt: '',
    work_location: '',
    work_location_country: 'LT',
    start_date: '',
    end_date: '',
    probation_period_months: 3,
    salary_amount: '',
    salary_currency: 'EUR',
    salary_type: 'monthly',
    working_hours_per_week: 40,
    vacation_days: 20,
    notice_period_days: 14,
    client_company_name: '',
    client_company_code: '',
    client_company_address: '',
    signed_by_employee: false,
    signed_by_employer: false,
    employee_signed_date: '',
    employer_signed_date: '',
    notes: ''
  });

  // Common positions in Lithuania
  const commonPositions = [
    { en: 'Warehouse Worker', lt: 'Sandėlio darbuotojas' },
    { en: 'Production Worker', lt: 'Gamybos darbuotojas' },
    { en: 'Cleaner', lt: 'Valytojas' },
    { en: 'Construction Worker', lt: 'Statybininkas' },
    { en: 'Driver', lt: 'Vairuotojas' },
    { en: 'Forklift Operator', lt: 'Krautuvo vairuotojas' },
    { en: 'Packer', lt: 'Pakuotojas' },
    { en: 'Assembly Worker', lt: 'Surinkėjas' },
    { en: 'Kitchen Helper', lt: 'Virtuvės pagalbininkas' },
    { en: 'Agricultural Worker', lt: 'Žemės ūkio darbuotojas' }
  ];

  useEffect(() => {
    fetchEmployees();
    generateContractNumber();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      // Filter only active employees
      const activeEmployees = response.data.filter(emp => emp.status === 'active');
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const generateContractNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({
      ...prev,
      contract_number: `CNT-${year}${month}-${random}`
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePositionSelect = (position) => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        position_title: position.en,
        position_title_lt: position.lt
      }));
    }
  };

  const handleEmployeeChange = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        employee_id: newValue.id
      }));
    }
  };

  const calculateEndDate = (startDate, months) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setDate(end.getDate() - 1); // Contract ends day before
    return end.toISOString().split('T')[0];
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      start_date: startDate,
      // Auto-calculate 6 month contract by default
      end_date: calculateEndDate(startDate, 6)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.employee_id) {
      setError('Please select an employee');
      return;
    }

    if (!formData.position_title || !formData.start_date || !formData.end_date) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.salary_amount || formData.salary_amount <= 0) {
      setError('Please enter a valid salary amount');
      return;
    }

    try {
      setLoading(true);
      await api.post('/contracts/create', formData);
      setSuccess('Contract created successfully!');
      
      setTimeout(() => {
        navigate('/admin/contracts');
      }, 2000);
    } catch (error) {
      console.error('Error creating contract:', error);
      setError(error.response?.data?.error || 'Failed to create contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/admin/contracts')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Create New Contract
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Contract Basic Information */}
          <Typography variant="h6" gutterBottom>
            Contract Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contract Number"
                name="contract_number"
                value={formData.contract_number}
                onChange={handleChange}
                required
                helperText="Auto-generated, can be modified"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Contract Type"
                name="contract_type"
                value={formData.contract_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="temporary">Temporary Employment</MenuItem>
                <MenuItem value="permanent">Permanent Employment</MenuItem>
                <MenuItem value="seasonal">Seasonal Work</MenuItem>
                <MenuItem value="project">Project Based</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.personal_code})`}
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee"
                    required
                    helperText="Only active employees shown"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Position Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Position Details
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={commonPositions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.en}
                onChange={(event, value) => handlePositionSelect(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position Title (English)"
                    name="position_title"
                    value={formData.position_title}
                    onChange={handleChange}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position Title (Lithuanian)"
                name="position_title_lt"
                value={formData.position_title_lt}
                onChange={handleChange}
                helperText="Lithuanian translation of position"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Work Location"
                name="work_location"
                value={formData.work_location}
                onChange={handleChange}
                required
                helperText="e.g., Vilnius, Kaunas, or specific address"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Work Country"
                name="work_location_country"
                value={formData.work_location_country}
                onChange={handleChange}
                required
              >
                <MenuItem value="LT">Lithuania</MenuItem>
                <MenuItem value="DE">Germany</MenuItem>
                <MenuItem value="NL">Netherlands</MenuItem>
                <MenuItem value="BE">Belgium</MenuItem>
                <MenuItem value="PL">Poland</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Contract Duration */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Contract Duration
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                helperText="Auto-calculated for 6 months"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Probation Period (months)"
                name="probation_period_months"
                type="number"
                value={formData.probation_period_months}
                onChange={handleChange}
                inputProps={{ min: 0, max: 6 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Notice Period (days)"
                name="notice_period_days"
                type="number"
                value={formData.notice_period_days}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          {/* Salary Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Compensation
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Salary Amount"
                name="salary_amount"
                type="number"
                value={formData.salary_amount}
                onChange={handleChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Currency"
                name="salary_currency"
                value={formData.salary_currency}
                onChange={handleChange}
                required
              >
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Salary Type"
                name="salary_type"
                value={formData.salary_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Hours/Week"
                name="working_hours_per_week"
                type="number"
                value={formData.working_hours_per_week}
                onChange={handleChange}
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Vacation Days/Year"
                name="vacation_days"
                type="number"
                value={formData.vacation_days}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          {/* Client Company (if applicable) */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Client Company (Optional)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Client Company Name"
                name="client_company_name"
                value={formData.client_company_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Code"
                name="client_company_code"
                value={formData.client_company_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Address"
                name="client_company_address"
                value={formData.client_company_address}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Signature Status */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Signature Status
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="signed_by_employee"
                    checked={formData.signed_by_employee}
                    onChange={handleChange}
                  />
                }
                label="Signed by Employee"
              />
              {formData.signed_by_employee && (
                <TextField
                  fullWidth
                  label="Employee Signed Date"
                  name="employee_signed_date"
                  type="date"
                  value={formData.employee_signed_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="signed_by_employer"
                    checked={formData.signed_by_employer}
                    onChange={handleChange}
                  />
                }
                label="Signed by Employer"
              />
              {formData.signed_by_employer && (
                <TextField
                  fullWidth
                  label="Employer Signed Date"
                  name="employer_signed_date"
                  type="date"
                  value={formData.employer_signed_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
          </Grid>

          {/* Notes */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Additional Notes
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                helperText="Any additional information about this contract"
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate('/admin/contracts')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Contract'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AddContract;
